import type {IssueFieldType} from '@github-ui/issue-fields-shared/issue-field'
import type {IssueFieldsUsageEvent} from '@github-ui/issue-fields-shared/issue-fields-analytics'
import {useEffect, useRef, useState} from 'react'

import styles from './FieldsSection.module.css'
import type {OptimisticIssueFieldValue} from './mutations/helpers'

// Custom hook for managing pinned fields animation
export function usePinnedFieldsAnimation(currentValue: string | null) {
  const [animatePinned, setAnimatePinned] = useState(false)
  const previousValue = useRef<string | null>(null)
  const hasMounted = useRef(false)
  const lastAnimationValue = useRef<string | null>(null)

  useEffect(() => {
    // Skip animation on mount, only animate on actual changes
    // Use lastAnimationValue instead of previousValue to avoid issues with re-renders
    if (hasMounted.current && currentValue !== lastAnimationValue.current && currentValue !== null) {
      // Reset first, then set to true to ensure animation triggers every time

      setAnimatePinned(false)
      lastAnimationValue.current = currentValue
      // Use setTimeout to ensure the reset happens before setting to true
      const startTimer = setTimeout(() => {
        setAnimatePinned(true)
      }, 0)

      // Turn off animation after it completes (2s duration)
      // This removes the animation classes so normal styling (and rounded corners) take over
      const endTimer = setTimeout(() => {
        setAnimatePinned(false)
      }, 2000)

      // Cleanup function
      return () => {
        clearTimeout(startTimer)
        clearTimeout(endTimer)
      }
    }

    // Initialize lastAnimationValue on mount to prevent flash
    if (!hasMounted.current) {
      lastAnimationValue.current = currentValue
    }

    previousValue.current = currentValue
    hasMounted.current = true
  }, [currentValue])

  return {animatePinned}
}

export type PinnedFieldPosition = 'only' | 'first' | 'middle' | 'last' | null

// Map position to CSS class names
const positionClassMap: Record<Exclude<PinnedFieldPosition, null>, string> = {
  only: styles.animationOnly,
  first: styles.animationFirst,
  middle: styles.animationMiddle,
  last: styles.animationLast,
}

// Shared utility for getting pinned field CSS classes
export function getPinnedFieldClassName(
  isPinned: boolean,
  animatePinned: boolean,
  position: PinnedFieldPosition = null,
): string | undefined {
  if (isPinned && animatePinned) {
    const positionClass = position ? positionClassMap[position] : ''
    return `${styles.pinnedField} ${styles.animationClass} ${positionClass}`.trim()
  }
  if (isPinned) {
    return styles.pinnedField
  }
  return undefined
}

/**
 * Calculates the position of a pinned field within the list of pinned fields.
 * Returns 'only' if there's only one pinned field, 'first'/'middle'/'last' based on position,
 * or null if the field is not pinned.
 */
export function getPinnedFieldPosition(
  fieldId: string,
  pinnedFieldIds: Set<string>,
  allFieldIds: string[],
): PinnedFieldPosition {
  if (!pinnedFieldIds.has(fieldId)) {
    return null
  }

  // Get only the pinned field IDs in order
  const orderedPinnedIds = allFieldIds.filter(id => pinnedFieldIds.has(id))
  const index = orderedPinnedIds.indexOf(fieldId)

  if (orderedPinnedIds.length === 1) {
    return 'only'
  }
  if (index === 0) {
    return 'first'
  }
  if (index === orderedPinnedIds.length - 1) {
    return 'last'
  }
  return 'middle'
}

// helper for mutation analytics and error handling
export type MutationAction = 'create' | 'update' | 'delete'

export type Mutation<TInput> = (args: {
  input: TInput
  fieldName?: string
  optimisticFieldValue?: OptimisticIssueFieldValue
  onCompleted: () => void
  onError: (error: unknown) => void
}) => void

export function runFieldMutation<TInput>({
  mutationFn,
  input,
  fieldName,
  optimisticFieldValue,
  action,
  fieldId,
  fieldType,
  inProjectsPage,
  ownerId,
  reportUsage,
  onError,
  onCompleted,
}: {
  mutationFn: Mutation<TInput>
  input: TInput
  fieldName?: string
  optimisticFieldValue?: OptimisticIssueFieldValue
  action: MutationAction
  fieldId: string
  fieldType: IssueFieldType
  inProjectsPage?: boolean
  ownerId?: number | null
  reportUsage: (event: IssueFieldsUsageEvent) => void
  onError?: (error: unknown) => void
  onCompleted?: () => void
}) {
  const startTime = Date.now()
  mutationFn({
    input,
    fieldName,
    optimisticFieldValue,
    onCompleted: () => {
      const duration = Date.now() - startTime
      reportUsage({
        page: inProjectsPage ? 'memex#show' : 'issue#show',
        action,
        fieldId,
        fieldType,
        status: 'success',
        duration,
        orgId: ownerId?.toString(),
      })
      onCompleted?.()
    },
    onError: error => {
      const duration = Date.now() - startTime
      reportUsage({
        page: inProjectsPage ? 'memex#show' : 'issue#show',
        action,
        fieldId,
        fieldType,
        status: 'error',
        errorDetails: error instanceof Error ? error.message : String(error),
        duration,
        orgId: ownerId?.toString(),
      })
      onError?.(error)
    },
  })
}
